import { gql, useMutation, useQuery } from '@apollo/client';
import type Quill from 'quill/core';
import React, { useImperativeHandle, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import Button from '../../components/Button';
import LoadingIndicator from '../../components/LoadingIndicator';
import type { ModalRef } from '../../components/ModalContainer';
import SVGIcon from '../../components/SVGIcon';
import EmojiAutocompleter from '../../editor/controls/EmojiAutocompleter';
import MentionPicker from '../../editor/controls/MentionPicker';
import SlashCommandMenu from '../../editor/controls/SlashCommandMenu';
import EditorToolbar from '../../editor/controls/toolbar/EditorToolbar';
import ImageToolbar from '../../editor/controls/toolbar/ImageToolbar';
import QuillComponent from '../../editor/QuillComponent';
import QuillContext from '../../editor/QuillContext';
import UPDATE_TEMPLATE from '../../graphql/updateTemplate';
import closeIcon from '../../icons/clear.svg';
import templateIcon from '../../icons/prefilled/template.svg';
import PostSkeleton from '../../post/PostSkeleton';
import useDeleteTemplateMutation from '../../templates/useDeleteTemplateMutation';
import UIActions from '../../ui/UIActions';
import useBeforeLeaveConfirm from '../../utils/useBeforeLeaveConfirm';
import styles from './UserTemplatesSettingsModal.module.scss';

const USER_TEMPLATES_SETTINGS_MODAL_QUERY = gql`
  query userTemplateSettingsModal($id: ID!) {
    template(id: $id) {
      id
      content
    }
  }
`;

interface UserTemplatesSettingsModalProps {
  id: string;
  close: () => void;
  modalRef: React.RefObject<ModalRef>;
}

const UserTemplatesSettingsModal = ({
  id,
  close,
  modalRef,
}: UserTemplatesSettingsModalProps) => {
  const dispatch = useDispatch();
  const [quill, setQuill] = useState<Quill | null>(null);

  const [hasChanged, setHasChanged] = useState(false);
  useBeforeLeaveConfirm({ skip: !hasChanged });

  useImperativeHandle(modalRef, () => ({
    allowClose() {
      return (
        !hasChanged ||
        // eslint-disable-next-line no-restricted-globals, no-alert
        confirm(
          'You have not yet saved your changes. Are you sure you want to discard them?',
        )
      );
    },
  }));

  const { data: { template } = {}, loading } = useQuery(
    USER_TEMPLATES_SETTINGS_MODAL_QUERY,
    {
      variables: { id },
      fetchPolicy: 'network-only',
    },
  );
  const templateContent = useMemo(
    () => (template ? template.content : null),
    [template],
  );

  const [deleteTemplate] = useDeleteTemplateMutation();

  const [updateTemplate] = useMutation(UPDATE_TEMPLATE);
  const [isSaving, setIsSaving] = useState(false);

  const handleDelete = () =>
    deleteTemplate({ variables: { id } }).then(() => {
      setHasChanged(false);
      close();
      dispatch(
        UIActions.pushToast({
          level: 'error',
          message: 'Template deleted',
          hideIcon: true,
        }),
      );
    });

  const handleUpdate = () => {
    setIsSaving(true);
    updateTemplate({
      variables: {
        id,
        content: JSON.stringify(quill?.getContents().ops),
      },
    }).then(() => {
      setHasChanged(false);
      close();
      dispatch(
        UIActions.pushToast({
          level: 'success',
          message: 'Template updated',
          hideIcon: true,
        }),
      );
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <div>
            <span className={styles.iconContainer}>
              <SVGIcon inline>{templateIcon}</SVGIcon>
            </span>
            <strong>Template</strong> · Visible to you only
          </div>
          <Button className={styles.mobileClose} onClick={close}>
            <SVGIcon>{closeIcon}</SVGIcon>
          </Button>
        </div>
        <div className={styles.editorContainer}>
          {template && (
            <div className={styles.editor}>
              <QuillContext>
                {quill && (
                  <>
                    <EditorToolbar readOnly={false} />
                    <MentionPicker quill={quill} readOnly={false} />
                    <EmojiAutocompleter quill={quill} readOnly={false} />
                    <ImageToolbar />
                  </>
                )}
                <QuillComponent
                  namespace="template"
                  content={templateContent}
                  titlePlaceholder="Untitled"
                  readOnly={false}
                  moduleConfig={{
                    headerCollapse: true,
                    hoveredBlotListener: true,
                    prefixListener: true,
                  }}
                  onMount={setQuill}
                  onTextChange={() => {
                    if (quill) {
                      setHasChanged(true);
                    }
                  }}
                />
                {quill && <SlashCommandMenu quill={quill} readOnly={false} />}
              </QuillContext>
            </div>
          )}
          {!template && loading && (
            <div className={styles.loader}>
              <PostSkeleton />
            </div>
          )}
        </div>
        <div className={styles.footer}>
          <Button variant="link" danger onClick={handleDelete}>
            Delete
          </Button>
          <Button
            variant="primary"
            disabled={!hasChanged || isSaving}
            onClick={handleUpdate}
          >
            {isSaving ? (
              <LoadingIndicator delay={0} adaptColor />
            ) : (
              'Save template'
            )}
          </Button>
        </div>
      </div>
      <Button className={styles.desktopClose} onClick={close}>
        <SVGIcon size="small">{closeIcon}</SVGIcon>
      </Button>
    </div>
  );
};

export default UserTemplatesSettingsModal;
